// HealStrokeCommand — undo/redo command for healing brush operations.
// Captures before/after pixel patches over a bounding box, exactly like
// PaintStrokeCommand but with a distinct kind for history labeling.

import type { Document } from "../document/Document";
import type { Layer, RasterLayer } from "../document/Layer";
import type { BBox } from "../pixel/brush";
import type { Command } from "./Command";

export class HealStrokeCommand implements Command {
  readonly kind = "heal-stroke";

  constructor(
    private readonly layerId: string,
    private readonly bbox: BBox,
    private readonly before: Uint8ClampedArray,
    private readonly after: Uint8ClampedArray,
  ) {}

  apply(doc: Document): Document {
    this.writePatch(doc, this.after);
    return doc;
  }

  invert(_before: Document, _after: Document): Command {
    return new HealStrokeCommand(this.layerId, this.bbox, this.after, this.before);
  }

  private writePatch(doc: Document, patch: Uint8ClampedArray): void {
    const layer = findRaster(doc.layers, this.layerId);
    if (!layer) return;
    const { x, y, w, h } = this.bbox;
    const data = layer.pixels.data;
    for (let row = 0; row < h; row++) {
      const dstOff = ((y + row) * layer.pixels.width + x) * 4;
      const srcOff = row * w * 4;
      data.set(patch.subarray(srcOff, srcOff + w * 4), dstOff);
    }
    layer.pixels.markDirty();
  }
}

export function snapshotRegion(layer: RasterLayer, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  const data = layer.pixels.data;
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * layer.pixels.width + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(data.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}

function findRaster(layers: readonly Layer[], id: string): RasterLayer | null {
  for (const l of layers) {
    // Match a `kind: "background"` layer too (kind-gap fix — mirrors
    // PaintStrokeCommand's findRaster). Without this, HealStrokeCommand's
    // apply/invert are no-ops on the Background layer: the Patch and Healing
    // Brush tools mutate the pixels directly so the heal is visible, but undo
    // (which routes through this lookup) silently does nothing.
    if (l.id === id && (l.kind === "raster" || l.kind === "background")) return l as RasterLayer;
    if (l.kind === "group") {
      const inner = findRaster(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}
